import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, FileText, Package, BookOpen, MapPin } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { searchEntries, type SearchCategory, type SearchEntry } from "@/lib/searchIndex";

const categoryLabels: Record<SearchCategory, { label: string; icon: typeof Search }> = {
  product: { label: "Product", icon: Package },
  resource: { label: "Resource", icon: BookOpen },
  page: { label: "Page", icon: FileText },
  location: { label: "Location", icon: MapPin },
};

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SearchDialog({ open, onOpenChange }: SearchDialogProps) {
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<SearchCategory | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      setQuery("");
      setSelectedCategory(null);
    }
  }, [open]);

  const filteredResults = useMemo(
    () => searchEntries(query, selectedCategory, 20),
    [query, selectedCategory]
  );

  const groupedResults = useMemo(() => {
    const groups: Record<string, SearchEntry[]> = {};
    filteredResults.forEach((result) => {
      if (!groups[result.category]) groups[result.category] = [];
      groups[result.category].push(result);
    });
    return groups;
  }, [filteredResults]);

  const handleSelect = (result: SearchEntry) => {
    onOpenChange(false);
    navigate(result.href);
  };

  const categories: SearchCategory[] = ["product", "resource", "page", "location"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-0">
        <DialogHeader className="sr-only">
          <DialogTitle>Search</DialogTitle>
        </DialogHeader>

        {/* Search Input */}
        <div className="flex items-center border-b px-4 py-3">
          <Search className="h-5 w-5 text-muted-foreground mr-3" />
          <Input
            placeholder="Search products, resources, pages..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="border-0 focus-visible:ring-0 px-0 text-base"
            autoFocus
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Category Filters */}
        <div className="flex gap-2 px-4 py-2 border-b">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-3 py-1 text-sm rounded-full transition-colors ${
              selectedCategory === null
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
              className={`px-3 py-1 text-sm rounded-full capitalize transition-colors ${
                selectedCategory === cat
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {categoryLabels[cat].label}s
            </button>
          ))}
        </div>

        {/* Results */}
        <ScrollArea className="max-h-[400px]">
          {query && filteredResults.length === 0 && (
            <div className="py-12 text-center text-muted-foreground">
              <Search className="h-10 w-10 mx-auto mb-4 opacity-50" />
              <p>No results found for "{query}"</p>
              <p className="text-sm mt-1">Try different keywords or browse categories</p>
            </div>
          )}

          {!query && (
            <div className="py-8 text-center text-muted-foreground">
              <Search className="h-10 w-10 mx-auto mb-4 opacity-50" />
              <p>Start typing to search</p>
              <p className="text-sm mt-1">Search products, resources, pages and locations</p>
            </div>
          )}

          {Object.entries(groupedResults).map(([category, results]) => {
            const CatIcon = categoryLabels[category as SearchCategory].icon;
            return (
              <div key={category} className="px-2 py-2">
                <div className="px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {categoryLabels[category as SearchCategory].label}s
                </div>
                {results.map((result) => (
                  <button
                    key={result.id}
                    onClick={() => handleSelect(result)}
                    className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-muted text-left transition-colors"
                  >
                    <CatIcon className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{result.title}</span>
                        <Badge variant="secondary" className="text-xs">
                          {categoryLabels[result.category].label}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {result.description}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            );
          })}
        </ScrollArea>

        {/* Keyboard Hints */}
        <div className="flex items-center justify-between px-4 py-2 border-t text-xs text-muted-foreground">
          <div className="flex gap-4">
            <span><kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">↵</kbd> to select</span>
            <span><kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">esc</kbd> to close</span>
          </div>
          <span>Press <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">⌘</kbd><kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">K</kbd> anytime to search</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
